import classcat from "classcat"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { v4 as uuid } from "uuid"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./OtherExpensesEditor.module.css"

import { OtherExpensesEditor_project$key } from "~/forms/ProjectBudgetForm/OtherExpenses/__generated__/OtherExpensesEditor_project.graphql"

import currency from "~/helpers/CurrencyHelper"
import { OtherCostWithCalculations } from "~/helpers/otherCosts"

import AddButton from "~/common/buttons/AddButton"

import ExpenseBudgetInput from "~/forms/ProjectBudgetForm/ExpenseBudgetInput"
import ExpenseTotalsRow, {
  ExpenseTotals,
} from "~/forms/ProjectBudgetForm/ExpenseTotalsRow"

import Column from "../TableColumn"
import Row from "../TableRow"
import { BudgetMethod } from "../types"

import OtherExpenseEditRow from "./OtherExpenseEditRow"

type Props = {
  expenses: Array<OtherCostWithCalculations>
  totalExpenses: ExpenseTotals
  expensesBudget: number
  budgetMethod: BudgetMethod
  project: OtherExpensesEditor_project$key
  onUpdate: (id: number | string, expense: OtherCostWithCalculations) => void
  onDelete: (id: number | string) => void
  onAdd: () => void
  onExpensesBudgetUpdate: (value: number) => void
  showNonBillableView?: boolean
}

export const createEmptyExpense = (): OtherCostWithCalculations => ({
  id: `new--${uuid()}`,
  date: new Date(),
  cost: 0,
  charge: 0,
  margin: 0,
  profit: 0,
  name: "",
})

const isEmptyExpense = (expense: OtherCostWithCalculations) => {
  return !expense.name?.trim() && expense.cost === 0 && expense.charge === 0
}

const OtherExpensesEditor = (props: Props) => {
  const {
    expenses,
    showNonBillableView = false,
    totalExpenses,
    expensesBudget,
    budgetMethod,
    onUpdate,
    onDelete,
    onAdd,
    onExpensesBudgetUpdate,
  } = props

  const project = useFragment<OtherExpensesEditor_project$key>(
    graphql`
      fragment OtherExpensesEditor_project on projects {
        id
        ...OtherExpenseEditRow_project
      }
    `,
    props.project,
  )

  const canDeleteRow =
    expenses.length > 1 ||
    (expenses.length === 1 && !isEmptyExpense(expenses[0]))

  const isPhaseMode = budgetMethod === "PHASE"

  return (
    <>
      {!showNonBillableView && !isPhaseMode && (
        <div className={styles.expensesHeader}>
          <div className={styles.label}>Expenses Budget</div>
          <ExpenseBudgetInput
            dataTest="expenses-budget_dollars"
            name="expenses-budget_dollars"
            currency={currency}
            onUpdate={onExpensesBudgetUpdate}
            value={expensesBudget}
            maxWidth={180}
            height={40}
          />
        </div>
      )}
      <div
        className={classcat([
          commonStyles.otherExpenseWrapper,
          {
            [commonStyles.phaseMode]: isPhaseMode,
          },
        ])}
      >
        <Row heading>
          <Column>Item</Column>
          {isPhaseMode && <Column>Phase</Column>}
          <Column textAlign="right">Date</Column>
          {!showNonBillableView && (
            <>
              <Column textAlign="right">Margin</Column>
              <Column textAlign="right">Gross Profit</Column>
            </>
          )}
          <Column textAlign="right">Cost</Column>
          {!showNonBillableView && <Column textAlign="right">Charge</Column>}
        </Row>
        <div className={commonStyles.tableBody}>
          {expenses.map((expense) => (
            <OtherExpenseEditRow
              key={`expense-${expense.id}`}
              expense={expense}
              showNonBillableView={showNonBillableView}
              isPhaseMode={isPhaseMode}
              project={project}
              currentPhaseId={expense.phaseId}
              canDelete={canDeleteRow}
              onUpdate={(updatedExpense) =>
                onUpdate(expense.id, updatedExpense)
              }
              onDelete={() => onDelete(expense.id)}
            />
          ))}
        </div>
      </div>
      <div className={commonStyles.tableWidget}>
        <Row style={{ display: "flex", padding: "10px 0" }}>
          <AddButton text="Add item" onClick={onAdd} />
        </Row>
      </div>
      <ExpenseTotalsRow
        showNonBillableView={showNonBillableView}
        totalExpenses={totalExpenses}
        title="Total Expenses"
      />
    </>
  )
}

export default OtherExpensesEditor
